import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import styles from "./PaymentMethodSelector.module.scss";
import { DeliveryType, PaymentMethod } from "../../../common/types/enums";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { getPaymentMethodIcon } from "../../../common/utils/functions/enumToIcon";
import { getPaymentMethodText } from "../../../common/utils/functions/enumToText";

interface Props {
  clusterId: number;
  paymentMethod: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  deliveryType: DeliveryType;
}

export const PaymentMethodSelector: React.FC<Props> = ({
  clusterId,
  paymentMethod,
  onSelect,
  deliveryType,
}) => {
  const clusters = useSelector(selectClusterList);
  const currentCluster = clusters.find((c) => Number(c.id) === clusterId);

  const payment = currentCluster?.payment;
  const isDelivery = deliveryType === DeliveryType.DELIVERY;

  const paymentOptions = useMemo(() => {
    const options: { method: PaymentMethod; label: string; Icon: any }[] = [];

    const cashEnabled = isDelivery
      ? payment?.cashOnDelivery
      : payment?.cashOnPickup;
    if (cashEnabled) {
      options.push({
        method: PaymentMethod.CASH_ON_DELIVERY,
        label: getPaymentMethodText(PaymentMethod.CASH_ON_DELIVERY),
        Icon: getPaymentMethodIcon(PaymentMethod.CASH_ON_DELIVERY),
      });
    }

    const cardEnabled = isDelivery
      ? payment?.cardOnDelivery
      : payment?.cardOnPickup;
    if (cardEnabled) {
      options.push({
        method: PaymentMethod.CARD_ON_DELIVERY,
        label: getPaymentMethodText(PaymentMethod.CARD_ON_DELIVERY),
        Icon: getPaymentMethodIcon(PaymentMethod.CARD_ON_DELIVERY),
      });
    }

    const onlineEnabled = isDelivery
      ? payment?.onlineOnDelivery
      : payment?.onlineOnPickup;
    if (onlineEnabled) {
      options.push({
        method: PaymentMethod.ONLINE,
        label: getPaymentMethodText(PaymentMethod.ONLINE),
        Icon: getPaymentMethodIcon(PaymentMethod.ONLINE),
      });
    }

    return options;
  }, [payment, isDelivery]);

  if (paymentOptions.length === 0) return null;

  return (
    <div className={styles.Container}>
      <h3 className={styles.Title}>Способ оплаты</h3>

      <div className={styles.Options}>
        {paymentOptions.map(({ method, label, Icon }) => {
          const isActive = paymentMethod === method;
          return (
            <button
              key={method}
              type="button"
              className={`${styles.Option} ${isActive ? styles.Active : ""}`}
              onClick={() => onSelect(method)}
            >
              {Icon && <Icon className={styles.Icon} />}
              <span className={styles.Label}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};